import {
  createSolidTable,
  flexRender,
  getCoreRowModel
} from '@tanstack/solid-table';
import { For, Match, Switch } from 'solid-js';
import * as Table from '~/components/ui/Table';
import {
  getMultiApplicationsTableColumnDef,
  handleDeleteApplication,
  MultiApplicationTabs
} from './utils';
import { useIntl } from '@cookbook/solid-intl';
import {
  PaginationActions,
  usePaginationContext
} from '~/components/Pagination';
import Skeleton from '~/components/ui/Skeleton';
import { useAppData } from '~/components/AppData';
import { useNavigate } from '@solidjs/router';
import {
  EnvironmentPagePath,
  getSlug,
  replacePathParams
} from '~/components/AppRouter/utils';
import { ApiConfigData } from '~/api/types/apiConfigData';
import SkLoader from '~/components/ui/SkLoader';
import { useToast } from '~/components/ui/Toast';
import { useConfirm } from '~/components/ui/Modal';
import { useQueryClient } from '@tanstack/solid-query';

export default function MultiApplicationsTable() {
  const intl = useIntl();
  const paginationContext = usePaginationContext();
  const appData = useAppData();
  const navigate = useNavigate();
  const toaster = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const table = createSolidTable({
    get data() {
      return (paginationContext.pageDetails?.data || []) as ApiConfigData[];
    },
    columns: getMultiApplicationsTableColumnDef({
      intl,
      onEditApplication: async (data: ApiConfigData) => {
        handleViewApplicationsDetailsNavigation(data.keyId);
      },
      onDeleteApplication: async (data: ApiConfigData) => {
        handleDeleteApplication({ data, intl, queryClient, toaster, confirm });
      }
    }),
    getCoreRowModel: getCoreRowModel()
  });

  const handleViewApplicationsDetailsNavigation = (applicationId: string) => {
    navigate(
      getSlug.environmentById(
        appData.selectedEnvironment?.id,
        replacePathParams(
          EnvironmentPagePath.ENVIRONMENT_MULTI_APPLICATION_TABS,
          {
            applicationId: applicationId,
            tabType: MultiApplicationTabs.CONFIGURATION
          }
        ) as EnvironmentPagePath,
        true
      )
    );
  };

  return (
    <Switch>
      <Match when={paginationContext.queryObject.isPending}>
        <SkLoader />
      </Match>
      <Match when={paginationContext.pageDetails.data.length === 0}>
        Empty
      </Match>
      <Match when={!!paginationContext.pageDetails.data.length}>
        <div class={`mt-6`}>
          <Table.Root class={'table-fixed text-sm'}>
            <Table.Thead class={`bg-bg-disabled`}>
              <Table.Tr>
                <For each={table.getFlatHeaders()}>
                  {(header) => {
                    const headerMeta: any = header.column.columnDef.meta || {};
                    return (
                      <Table.Th class={headerMeta?.class || ''}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Table.Th>
                    );
                  }}
                </For>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <For each={table.getRowModel().rows}>
                {(row, index) => {
                  return (
                    <Table.Tr
                      onclick={() => {
                        handleViewApplicationsDetailsNavigation(
                          row.original.keyId
                        );
                      }}
                      class={`border-b-gray-200 hover:bg-bg-subtle cursor-pointer`}
                      classList={{
                        'border-t-0': index() === 0,
                        '!border-t-border-subtle': index() !== 0
                      }}
                    >
                      <For each={row.getVisibleCells()}>
                        {(cell) => {
                          return (
                            <Table.Td class={`py-3`}>
                              <Skeleton
                                class={`w-1/2 h-[21px]`}
                                isLoading={
                                  paginationContext.queryObject.isFetching
                                }
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </Skeleton>
                            </Table.Td>
                          );
                        }}
                      </For>
                    </Table.Tr>
                  );
                }}
              </For>
            </Table.Tbody>
          </Table.Root>
          <PaginationActions />
        </div>
      </Match>
    </Switch>
  );
}
